import type { ComposeAttachment } from "./resources.ts";

// Turning a volunteer's flyer into something the compose API will accept.
// Phones produce 4-12 MB photos; the API caps a whole request at 4.5 MB and
// re-sends every attachment on each revision, so images get downscaled here
// rather than at the server. PDFs pass through — Claude reads them natively
// and rasterizing one in the browser would mean shipping pdf.js to the SPA.

export const ACCEPTED_TYPES =
  "image/png,image/jpeg,image/webp,image/gif,application/pdf";

/** Keep in step with MAX_ATTACHMENTS / MAX_ATTACHMENT_BYTES in
 *  api/src/functions/compose-slide.ts — these give the friendlier error. */
export const MAX_ATTACHMENTS = 4;
export const MAX_TOTAL_BYTES = 4_500_000;

/** Long edge, in px, for a downscaled flyer. Claude's high-resolution tier
 *  charges by pixel area and tops out around 4784 tokens per image; ~1800
 *  keeps small print (dates, addresses, phone numbers) legible while landing
 *  most photos well under the token cap. */
const MAX_EDGE = 1800;
const JPEG_QUALITY = 0.85;

/** Below this an image is already small enough to send untouched, which avoids
 *  a needless re-encode (and keeps PNG text crisp). */
const PASSTHROUGH_BYTES = 900_000;

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

export function isAccepted(type: string): boolean {
  return type === "application/pdf" || IMAGE_TYPES.includes(type);
}

export function attachmentBytes(a: ComposeAttachment): number {
  const padding = a.data.endsWith("==") ? 2 : a.data.endsWith("=") ? 1 : 0;
  return Math.floor((a.data.length * 3) / 4) - padding;
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read that file"));
    reader.onload = () => {
      const url = String(reader.result);
      // "data:<mime>;base64,<payload>" — the API wants just the payload.
      const comma = url.indexOf(",");
      resolve(comma >= 0 ? url.slice(comma + 1) : url);
    };
    reader.readAsDataURL(blob);
  });
}

async function downscale(file: File): Promise<{ blob: Blob; type: string }> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= PASSTHROUGH_BYTES) {
      return { blob: file, type: file.type };
    }
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return { blob: file, type: file.type };
    // JPEG has no alpha; without this, a transparent PNG flyer comes out on
    // black and the model reads dark text against it.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    return blob ? { blob, type: "image/jpeg" } : { blob: file, type: file.type };
  } finally {
    bitmap.close();
  }
}

/** Read one picked file into a wire-ready attachment. Throws a message meant
 *  to be shown to the volunteer as-is. */
export async function readAttachment(file: File): Promise<ComposeAttachment> {
  if (!isAccepted(file.type)) {
    throw new Error(
      `"${file.name}" is a ${file.type || "unknown"} file. Attach a PNG, JPEG, WebP, GIF, or PDF.`,
    );
  }

  if (file.type === "application/pdf") {
    if (file.size > MAX_TOTAL_BYTES) {
      throw new Error(
        `"${file.name}" is ${(file.size / 1e6).toFixed(1)} MB — too large. Export the PDF at a smaller size, or attach a photo of it instead.`,
      );
    }
    return {
      mediaType: "application/pdf",
      data: await toBase64(file),
      name: file.name,
    };
  }

  const { blob, type } = await downscale(file);
  return { mediaType: type, data: await toBase64(blob), name: file.name };
}

/**
 * Pull a piece of existing content into an attachment, so a slide already in
 * the library can be fed back to the composer — chiefly a page of the weekly
 * PowerPoint, which is already sitting in blob storage as a PNG.
 *
 * The URL is a short-lived read-SAS the API hands out, and blob CORS allows GET
 * from both site origins, so the browser can fetch it directly. From there it
 * goes through the same downscale-and-encode path as a picked file.
 */
export async function attachmentFromUrl(
  url: string,
  name: string,
): Promise<ComposeAttachment> {
  let res: Response;
  try {
    res = await fetch(url);
  } catch {
    // Almost always an expired SAS or a missing CORS origin, neither of which
    // is worth explaining to a volunteer.
    throw new Error(`Couldn't load "${name}". Try reloading the page.`);
  }
  if (!res.ok) throw new Error(`Couldn't load "${name}" (${res.status}).`);

  const blob = await res.blob();
  // Blob storage usually reports a type; fall back to the extension.
  const type =
    blob.type && blob.type !== "application/octet-stream"
      ? blob.type
      : /\.jpe?g$/i.test(url)
        ? "image/jpeg"
        : /\.webp$/i.test(url)
          ? "image/webp"
          : /\.gif$/i.test(url)
            ? "image/gif"
            : /\.pdf$/i.test(url)
              ? "application/pdf"
              : "image/png";
  return readAttachment(new File([blob], name, { type }));
}
